/**
 * Dashboard Designer — field catalog + saved-layout model (frontend).
 *
 * This is the UI-first, in-browser representation of what will become two DB
 * tables in a later phase (see the Dashboard Designer ADR):
 *
 *   dbo.AI_DashboardFieldCatalog  → FIELD_CATALOG  (what CAN be shown per view)
 *   dbo.AI_DashboardDefinition    → DashDefinition (what IS shown, per Tenant+Division)
 *
 * Nothing here talks to the API yet.  `loadDefinition` / `saveDefinition`
 * currently persist to localStorage so the designer is fully usable on its own;
 * Phase 2 swaps those two functions for GET/PUT /api/v1/dashboard/definition
 * with ZERO change to the page component.  The catalog fields mirror the columns
 * the backlog SPs already return (SampleBacklogPage COLS) plus a few extra
 * "available" fields to demonstrate adding a brand-new column.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

/** Backlog dashboards a client can customise (subset of SampleBacklogPage views;
 *  the pilot rolls the rest in as their wide SPs land). */
export type DashViewId =
  | 'missing-coc' | 'due-samples' | 'sample-tracking' | 'sample-prep'
  | 'sdms-result-entry' | 'data-review' | 'reporting' | 'result-approval';

/** How a column value is rendered (drives fmtLocal* / alignment / pill). */
export type FieldFormat = 'text' | 'number' | 'date' | 'datetime' | 'pill';

/** One field that CAN be surfaced on a view (catalog entry). */
export interface CatalogField {
  key: string;
  label: string;
  format: FieldFormat;
  defaultWidth: number;
}

/** One column as configured in a saved layout (order = array order). */
export interface DashColumn {
  key: string;
  caption: string;
  width: number;
  format: FieldFormat;
}

/** One KPI card toggle in a saved layout. */
export interface DashKpi {
  key: string;
  label: string;
  enabled: boolean;
}

/** A complete saved dashboard layout — the JSON that will live in
 *  AI_DashboardDefinition.DefinitionJson.  `charts` is reserved for a later
 *  (Tier B) phase and stays empty for now, so the model never needs a rewrite. */
export interface DashDefinition {
  columns: DashColumn[];
  kpis: DashKpi[];
  charts?: unknown[];
}

export interface DashView {
  id: DashViewId;
  label: string;
  icon: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Views selectable in the designer
// ─────────────────────────────────────────────────────────────────────────────
export const DASH_VIEWS: DashView[] = [
  { id: 'missing-coc',       label: '00 — Missing COC',            icon: 'ti-alert-circle' },
  { id: 'due-samples',       label: 'BL 00 — Due Samples',    icon: 'ti-clock' },
  { id: 'sample-tracking',   label: 'BL 01 — Sample Tracking',icon: 'ti-map-pin' },
  { id: 'sample-prep',       label: 'BL 03 — Sample Prep',    icon: 'ti-test-pipe' },
  { id: 'sdms-result-entry', label: 'BL 04 — SDMS Result Entry', icon: 'ti-table' },
  { id: 'data-review',       label: 'BL 05 — Data Review',    icon: 'ti-checkup-list' },
  { id: 'reporting',         label: 'BL 06 — Reporting',      icon: 'ti-report' },
  { id: 'result-approval',   label: 'Result Approval',             icon: 'ti-circle-check' },
];

// ─────────────────────────────────────────────────────────────────────────────
// Field catalog — every field a client may add to each view.
// Fields marked "(extra)" are NOT shown by default: they demonstrate adding a
// brand-new column from the wide SP without a code release.
// ─────────────────────────────────────────────────────────────────────────────
export const FIELD_CATALOG: Record<DashViewId, CatalogField[]> = {
  'missing-coc': [
    { key: 'jobId',        label: 'Job ID',        format: 'text',     defaultWidth: 110 },
    { key: 'projectId',    label: 'Project ID',    format: 'text',     defaultWidth: 160 },
    { key: 'client',       label: 'Client',        format: 'text',     defaultWidth: 200 },
    { key: 'receivedDate', label: 'Date Received', format: 'datetime', defaultWidth: 170 },
    // extra available fields
    { key: 'projectName',  label: 'Project Name',  format: 'text',     defaultWidth: 180 },
    { key: 'clientContact',label: 'Client Contact',format: 'text',     defaultWidth: 160 },
    { key: 'poNumber',     label: 'PO Number',     format: 'text',     defaultWidth: 130 },
    { key: 'ageDays',      label: 'Age (days)',    format: 'number',   defaultWidth: 100 },
  ],
  'due-samples': [
    { key: 'jobId',        label: 'Job ID',      format: 'text',   defaultWidth: 110 },
    { key: 'sampleId',     label: 'Sample ID',   format: 'text',   defaultWidth: 120 },
    { key: 'sampleName',   label: 'Sample Name', format: 'text',   defaultWidth: 160 },
    { key: 'testName',     label: 'Test',        format: 'text',   defaultWidth: 180 },
    { key: 'method',       label: 'Method',      format: 'text',   defaultWidth: 120 },
    { key: 'dueDate',      label: 'Due Date',    format: 'date',   defaultWidth: 100 },
    { key: 'receivedDate', label: 'Received',    format: 'date',   defaultWidth: 100 },
    { key: 'client',       label: 'Client',      format: 'text',   defaultWidth: 200 },
    { key: 'projectId',    label: 'Project ID',  format: 'text',   defaultWidth: 120 },
    { key: 'analyst',      label: 'Analyst',     format: 'text',   defaultWidth: 140 },
    { key: 'turnaroundDays', label: 'Turnaround (days)', format: 'number', defaultWidth: 130 },
  ],
  'sample-tracking': [
    { key: 'jobId',        label: 'Job ID',        format: 'text',     defaultWidth: 110 },
    { key: 'client',       label: 'Client',        format: 'text',     defaultWidth: 200 },
    { key: 'projectId',    label: 'Project ID',    format: 'text',     defaultWidth: 150 },
    { key: 'receivedDate', label: 'Date Received', format: 'datetime', defaultWidth: 170 },
    { key: 'dueDate',      label: 'Due Date',      format: 'date',     defaultWidth: 100 },
    { key: 'sampleCount',  label: 'Samples',       format: 'number',   defaultWidth: 90 },
    { key: 'testCount',    label: 'Tests',         format: 'number',   defaultWidth: 80 },
    { key: 'projectName',  label: 'Project Name',  format: 'text',     defaultWidth: 180 },
  ],
  'sample-prep': [
    { key: 'jobId',        label: 'Job ID',       format: 'text',     defaultWidth: 110 },
    { key: 'sampleId',     label: 'Sample ID',    format: 'text',     defaultWidth: 140 },
    { key: 'matrix',       label: 'Matrix',       format: 'text',     defaultWidth: 130 },
    { key: 'testName',     label: 'Test',         format: 'text',     defaultWidth: 200 },
    { key: 'method',       label: 'Method',       format: 'text',     defaultWidth: 120 },
    { key: 'prepMethod',   label: 'Prep Method',  format: 'text',     defaultWidth: 120 },
    { key: 'prepType',     label: 'Prep Type',    format: 'pill',     defaultWidth: 150 },
    { key: 'receivedDate', label: 'Received Date',format: 'datetime', defaultWidth: 150 },
    { key: 'client',       label: 'Client',       format: 'text',     defaultWidth: 180 },
  ],
  'sdms-result-entry': [
    { key: 'jobId',              label: 'Job ID',              format: 'text',     defaultWidth: 110 },
    { key: 'sampleId',           label: 'Sample ID',           format: 'text',     defaultWidth: 140 },
    { key: 'testName',           label: 'Test',                format: 'text',     defaultWidth: 180 },
    { key: 'holdtimeExpiryDate', label: 'Holdtime Expiry',     format: 'text',     defaultWidth: 170 },
    { key: 'dateCollected',      label: 'Date Collected',      format: 'datetime', defaultWidth: 170 },
    { key: 'receivedDate',       label: 'Received Date',       format: 'datetime', defaultWidth: 160 },
    { key: 'projectId',          label: 'Project ID',          format: 'text',     defaultWidth: 120 },
    { key: 'client',             label: 'Client',              format: 'text',     defaultWidth: 200 },
    { key: 'status',             label: 'Status',              format: 'pill',     defaultWidth: 160 },
  ],
  'data-review': [
    { key: 'jobId',         label: 'Job ID',        format: 'text',     defaultWidth: 110 },
    { key: 'qCBatchID',     label: 'QC Batch ID',   format: 'text',     defaultWidth: 130 },
    { key: 'sysSampleID',   label: 'Sys Sample ID', format: 'text',     defaultWidth: 150 },
    { key: 'sampleName',    label: 'Sample Name',   format: 'text',     defaultWidth: 160 },
    { key: 'testName',      label: 'Test',          format: 'text',     defaultWidth: 170 },
    { key: 'method',        label: 'Method',        format: 'text',     defaultWidth: 120 },
    { key: 'parameter',     label: 'Parameter',     format: 'text',     defaultWidth: 160 },
    { key: 'resultNumeric', label: 'Result Numeric',format: 'number',   defaultWidth: 120 },
    { key: 'result',        label: 'Result',        format: 'text',     defaultWidth: 120 },
    { key: 'unitName',      label: 'Unit',          format: 'text',     defaultWidth: 100 },
    { key: 'analyzedDate',  label: 'Analyzed Date', format: 'datetime', defaultWidth: 160 },
    { key: 'analyzedBy',    label: 'Analyzed By',   format: 'text',     defaultWidth: 150 },
  ],
  'reporting': [
    { key: 'jobId',         label: 'Job ID',        format: 'text',     defaultWidth: 110 },
    { key: 'dateCollected', label: 'Date Collected',format: 'datetime', defaultWidth: 160 },
    { key: 'receivedDate',  label: 'Received Date',  format: 'datetime', defaultWidth: 160 },
    { key: 'projectID',     label: 'Project ID',     format: 'text',    defaultWidth: 180 },
    { key: 'projectName',   label: 'Project Name',   format: 'text',    defaultWidth: 180 },
    { key: 'client',        label: 'Client',         format: 'text',    defaultWidth: 200 },
    { key: 'dueDate',       label: 'Due Date',       format: 'date',    defaultWidth: 120 },
    { key: 'status',        label: 'Status',         format: 'pill',    defaultWidth: 160 },
  ],
  'result-approval': [
    { key: 'jobId',         label: 'Job ID',        format: 'text',     defaultWidth: 110 },
    { key: 'client',        label: 'Client',        format: 'text',     defaultWidth: 200 },
    { key: 'projectId',     label: 'Project ID',    format: 'text',     defaultWidth: 150 },
    { key: 'projectName',   label: 'Project Name',  format: 'text',     defaultWidth: 180 },
    { key: 'test',          label: 'Test',          format: 'text',     defaultWidth: 180 },
    { key: 'method',        label: 'Method',        format: 'text',     defaultWidth: 130 },
    { key: 'dateCollected', label: 'Date Collected',format: 'datetime', defaultWidth: 160 },
    { key: 'receivedDate',  label: 'Received Date',  format: 'datetime',defaultWidth: 160 },
    { key: 'status',        label: 'Status',         format: 'pill',    defaultWidth: 160 },
  ],
};

// ─────────────────────────────────────────────────────────────────────────────
// KPI catalog — the KPI cards each view can show (labels mirror computeKpis()).
// ─────────────────────────────────────────────────────────────────────────────
export const KPI_CATALOG: Record<DashViewId, DashKpi[]> = {
  'missing-coc':       kpis('Missing COC', 'Oldest', 'This Week', 'Clients'),
  'due-samples':       kpis('Due Samples', 'Most Overdue', 'Due This Week', 'Clients'),
  'sample-tracking':   kpis('Jobs', 'Samples', 'Tests', 'Clients'),
  'sample-prep':       kpis('Tests Pending', 'Oldest', 'Jobs', 'Clients'),
  'sdms-result-entry': kpis('Pending Entry', 'Overdue', 'Jobs', 'Clients'),
  'data-review':       kpis('Pending Review', 'Analysts', 'Jobs', 'Clients'),
  'reporting':         kpis('Jobs', 'Pending Tests', 'Overdue', 'Clients'),
  'result-approval':   kpis('Pending Approval', 'Jobs', 'Overdue', 'Clients'),
};

function kpis(...labels: string[]): DashKpi[] {
  return labels.map(l => ({ key: l.toLowerCase().replace(/[^a-z0-9]+/g, '-'), label: l, enabled: true }));
}

// ─────────────────────────────────────────────────────────────────────────────
// Default layout per view — the first N catalog fields shown, in catalog order,
// matching the current hardcoded SampleBacklogPage COLS.
// ─────────────────────────────────────────────────────────────────────────────
const DEFAULT_SHOWN: Record<DashViewId, string[]> = {
  'missing-coc':       ['jobId', 'projectId', 'client', 'receivedDate'],
  'due-samples':       ['jobId', 'sampleId', 'sampleName', 'testName', 'method', 'dueDate', 'receivedDate', 'client', 'projectId'],
  'sample-tracking':   ['jobId', 'client', 'projectId', 'receivedDate', 'dueDate', 'sampleCount', 'testCount'],
  'sample-prep':       ['jobId', 'sampleId', 'matrix', 'testName', 'method', 'prepMethod', 'prepType', 'receivedDate'],
  'sdms-result-entry': ['jobId', 'sampleId', 'testName', 'holdtimeExpiryDate', 'dateCollected', 'receivedDate', 'projectId', 'client', 'status'],
  'data-review':       ['jobId', 'qCBatchID', 'sysSampleID', 'sampleName', 'testName', 'method', 'parameter', 'resultNumeric', 'result', 'unitName', 'analyzedDate', 'analyzedBy'],
  'reporting':         ['jobId', 'dateCollected', 'receivedDate', 'projectID', 'projectName', 'client', 'dueDate', 'status'],
  'result-approval':   ['jobId', 'client', 'projectId', 'projectName', 'test', 'method', 'dateCollected', 'receivedDate', 'status'],
};

/** The out-of-the-box layout for a view (used on first load and "Reset"). */
export function defaultDefinition(view: DashViewId): DashDefinition {
  const cat = FIELD_CATALOG[view];
  const byKey = new Map(cat.map(f => [f.key, f]));
  const columns: DashColumn[] = DEFAULT_SHOWN[view]
    .map(k => byKey.get(k))
    .filter((f): f is CatalogField => !!f)
    .map(f => ({ key: f.key, caption: f.label, width: f.defaultWidth, format: f.format }));
  return { columns, kpis: KPI_CATALOG[view].map(k => ({ ...k })), charts: [] };
}

// ─────────────────────────────────────────────────────────────────────────────
// Persistence — localStorage stub for the UI-first phase.
// Phase 2: replace both with the config API (GET/PUT /api/v1/dashboard/definition),
// keeping this exact signature so the page component does not change.
// ─────────────────────────────────────────────────────────────────────────────
const LS_KEY = (view: DashViewId) => `alpaca.dashboard.definition.${view}`;

export function loadDefinition(view: DashViewId): DashDefinition {
  try {
    const raw = localStorage.getItem(LS_KEY(view));
    if (raw) return JSON.parse(raw) as DashDefinition;
  } catch { /* fall through to default — a bad/blocked store is not fatal */ }
  return defaultDefinition(view);
}

export async function saveDefinition(view: DashViewId, def: DashDefinition): Promise<void> {
  // Simulated latency so the shared blocking loader is visible, matching a real save.
  await new Promise(r => setTimeout(r, 250));
  try {
    localStorage.setItem(LS_KEY(view), JSON.stringify(def));
  } catch {
    throw new Error('Could not save the dashboard layout in this browser.');
  }
}
